import { z } from 'zod'
import type { ApiClient } from './client'
import { ackEnvelope, assertAck } from './envelope'

/**
 * Signals — the output of detections, stored in the SIGNALS stream and served
 * by the legacy core API.
 *
 *     POST /{clusterId}/api/signals/list               paged list, addfilters
 *     POST /{clusterId}/api/signals/data               one full signal document
 *     POST /{clusterId}/api/signals/timeslice_data     counts per bucket
 *     POST /{clusterId}/api/signals/get_kb             detection knowledge base
 *     POST /{clusterId}/api/signals/case_list          cases holding a signal
 *     POST /{clusterId}/api/signals/add_signal_remark  free-text remark
 *     POST /{clusterId}/api/signals/add_rule           flag signals false-positive
 *     POST /{clusterId}/api/signals/tag/list           the scope's signal tags
 *     POST /{clusterId}/api/signals/tag/create         add a signal tag to the scope
 *     POST /{clusterId}/api/models/signals_attribution campaigns behind a connected-signals subgraph
 *     POST /{clusterId}/api/signals/facets/task_id     start the raw-event task behind a signal
 *     POST /{clusterId}/api/signals/facets/result      field breakdown of that task's events
 *     POST /{clusterId}/wrk/api/job/facetrevoke        stop that task
 *
 * READ paths VERIFIED 2026-08-24 against the gotham scope of ap1
 * (docs/inventory/signals-capture.json, tools/inventory/capture_signals.mjs):
 * list, data, timeslice_data, get_kb, case_list, tag/list all answer the
 * `{ TStamp, count, data, message, status }` envelope. Facts the capture
 * established:
 *   - Row fields are `$`-prefixed PascalCase ($Uuid, $CNAMTime, $SuspectUser);
 *     the SIGNALS stream serves the same fields bare lowercase. Rows are
 *     returned RAW and lib/signal-model readSignal owns the mapping.
 *   - `$SourceStream` is a real array (the stream serves a JSON string).
 *   - timeslice rows: { count, start_time, end_time } in EPOCH SECONDS.
 *   - get_kb `data` is the markdown string itself.
 *   - case_list `data` is a MAP keyed by signal uuid → case rows.
 *   - No `raw_event_query` field exists on live rows — only the demo/workbook
 *     emission contract carries it; live raw-event pivots must be built from
 *     entities + streams.
 * ⚠️ Still unverified (write paths, not exercised read-only):
 * add_signal_remark, add_rule, facets/* (facets shapes are read from the
 * legacy drawer's code, never captured — every schema passes through).
 */

export type SignalRow = Record<string, unknown>

const rowsIn = z.union([
  z.array(z.record(z.string(), z.unknown())),
  z.object({ result: z.array(z.record(z.string(), z.unknown())).default([]) }),
])

/** `data` is either the array or `{ result: [...] }` — both envelopes exist
 * on this backend (envelope.ts). Normalised to the array here. */
export const signalListResponse = z
  .object({
    status: z.string().optional(),
    message: z.string().optional(),
    count: z.number().optional(),
    data: rowsIn.default([]),
  })
  .transform((r) => ({
    status: r.status,
    message: r.message,
    count: r.count ?? null,
    rows: Array.isArray(r.data) ? r.data : r.data.result,
  }))

export const signalDataResponse = z
  .object({
    status: z.string().optional(),
    data: z.union([z.record(z.string(), z.unknown()), z.array(z.record(z.string(), z.unknown()))]).default({}),
  })
  .transform((r) => (Array.isArray(r.data) ? (r.data[0] ?? {}) : r.data))

/** Timeslice rows: `{ <bucket>: epoch, count }` under any of the legacy names. */
export const signalTimesliceResponse = z
  .object({
    status: z.string().optional(),
    data: rowsIn.default([]),
  })
  .transform((r) => (Array.isArray(r.data) ? r.data : r.data.result))

/**
 * case_list: `data` maps signal uuid → case rows (VERIFIED live). An array
 * (legacy-doc guess) is tolerated and folded under the requested key order.
 */
export const signalCaseListResponse = z
  .object({
    status: z.string().optional(),
    data: z
      .union([
        z.record(z.string(), z.array(z.record(z.string(), z.unknown()))),
        z.array(z.record(z.string(), z.unknown())),
      ])
      .default({}),
  })
  .transform((r) => (Array.isArray(r.data) ? { '': r.data } : r.data))

export const signalKbResponse = z
  .object({
    status: z.string().optional(),
    data: z.union([z.string(), z.object({ kb: z.string().optional(), signal_kb: z.string().optional() }).passthrough()]).default(''),
  })
  .transform((r) => (typeof r.data === 'string' ? r.data : (r.data.kb ?? r.data.signal_kb ?? '')))

/**
 * facets/task_id — UNVERIFIED (no capture; shape from what the legacy signal
 * drawer reads, components/common/signal-context.js). `data` is `{ id }` when
 * the signal has a raw-event query, `{}` when it has none.
 */
export const facetTaskResponse = z
  .object({
    status: z.string().default(''),
    message: z.string().nullable().optional(),
    data: z
      .union([z.object({ id: z.union([z.string(), z.number()]).optional() }).passthrough(), z.array(z.unknown()), z.null()])
      .default({}),
  })
  .passthrough()

/** One field's breakdown over a signal's raw events. */
export interface SignalFacet {
  field: string
  /** Distinct values the field took. */
  distinct: number
  /** Values with their event counts, server order (most frequent first). */
  values: { value: string; count: number }[]
}

/**
 * facets/result — UNVERIFIED (no capture). The legacy drawer reads each row as
 * `{ unique, count, result: [{ "<value>": n }, …] }`: `unique` is the label it
 * shows (the field name), `count` the number of distinct values, `result`
 * single-key objects of value → events.
 */
export const facetResultResponse = z
  .object({
    status: z.string().default(''),
    message: z.string().nullable().optional(),
    data: z
      .array(
        z
          .object({
            unique: z.union([z.string(), z.number()]).transform(String).default(''),
            count: z.number().nullable().optional(),
            result: z.array(z.record(z.string(), z.union([z.number(), z.string()]))).default([]),
          })
          .passthrough(),
      )
      .nullable()
      .default([]),
  })
  .passthrough()

export function readSignalFacets(data: z.output<typeof facetResultResponse>['data']): SignalFacet[] {
  return (data ?? [])
    .filter((r) => r.unique !== '')
    .map((r) => {
      const values = r.result.flatMap((o) => Object.entries(o).map(([value, n]) => ({ value, count: Number(n) || 0 })))
      return { field: r.unique, distinct: typeof r.count === 'number' ? r.count : values.length, values }
    })
}

/** A facets call the server refused (`failed` / `denied` / `invalid`), with its message. */
export class SignalFacetsError extends Error {}

const mutationResponse = z.object({
  status: z.string().optional(),
  message: z.string().optional(),
})

/**
 * models/signals_attribution — the campaigns a connected-signals subgraph
 * matches, from the detection → campaign mapping. Read from the handler and
 * its helper (2026-10-07), UNVERIFIED live: `data: {campaigns: [...] | null}`,
 * at most 5, each `{campaign_name, likelihood (0–100, 2 dp), mitre_attack_data:
 * {tactic: [technique]}, description}`. A failed attribution answers
 * `data: {}` with status 200 (CB-69), so a missing `campaigns` key is a
 * failure, not "no match" — `readSignalAttribution` throws on it and on any
 * non-success status. A bad campaign drops itself.
 */
const attributionCampaignWire = z
  .object({
    campaign_name: z.string().min(1),
    likelihood: z.union([z.number(), z.string()]).transform(Number).pipe(z.number().finite()).catch(0),
    mitre_attack_data: z.record(z.string(), z.array(z.string()).catch([])).catch({}),
    description: z.string().nullable().catch(null),
  })
  .passthrough()

export interface AttributionCampaign {
  name: string
  /** Share of the subgraph's techniques the campaign explains, 0–100. */
  likelihood: number
  /** tactic → techniques, as the server grouped them. */
  techniques: Record<string, string[]>
  description: string
}

export const signalAttributionResponse = z
  .object({
    status: z.string().optional(),
    message: z.string().nullable().optional().catch(null),
    /** null: no payload (`data` missing, not an object, or without `campaigns`) — a failure. */
    data: z
      .object({ campaigns: z.array(attributionCampaignWire.nullable().catch(null)).nullable() })
      .passthrough()
      .nullable()
      .catch(null)
      .default(null),
  })
  .passthrough()

/** A refused or empty-handed attribution (non-success status, or no `campaigns` in `data`). */
export class SignalAttributionError extends Error {}

/** The campaigns; `campaigns: []` / `null` read as no match. Throws on a failure (see above). */
export function readSignalAttribution(res: z.output<typeof signalAttributionResponse>): AttributionCampaign[] {
  if (res.status !== undefined && res.status !== 'success') {
    throw new SignalAttributionError(res.message || 'Campaign attribution failed.')
  }
  if (res.data === null) throw new SignalAttributionError(res.message || 'Campaign attribution returned no result.')
  return (res.data.campaigns ?? [])
    .filter((c): c is z.output<typeof attributionCampaignWire> => c !== null)
    .map((c) => ({ name: c.campaign_name, likelihood: c.likelihood, techniques: c.mitre_attack_data, description: c.description ?? '' }))
}

/**
 * signals/tag/list — the scope's signal tags. Shape from the 2026-08 capture
 * (signals-capture.md): `data: [{ tag_id, tag_value, tag_type, tag_color,
 * enabled, scope_id }]`. Rows without a value are dropped.
 */
const signalTagWire = z
  .object({
    tag_id: z.union([z.string(), z.number()]).transform(String).optional(),
    tag_value: z.string().optional(),
    tag_type: z.string().optional(),
    tag_color: z.string().optional(),
    enabled: z.boolean().optional(),
  })
  .passthrough()

export interface SignalTag {
  id: string
  value: string
  type: string
  color: string
  enabled: boolean
}

export const signalTagListResponse = z
  .object({
    status: z.string().optional(),
    data: z.array(signalTagWire).catch([]).default([]),
  })
  .transform((r) => ({
    data: r.data
      .filter((t) => typeof t.tag_value === 'string' && t.tag_value !== '')
      .map((t): SignalTag => ({
        id: t.tag_id ?? (t.tag_value as string),
        value: t.tag_value as string,
        type: t.tag_type ?? '',
        color: t.tag_color ?? '',
        enabled: t.enabled ?? true,
      })),
  }))

export interface SignalScope {
  clusterId: string
  scopeId: string
}

export interface SignalListQuery extends SignalScope {
  /** DQL-style duration ('7d') — the legacy default. Absolute times win when set. */
  duration?: string
  startTime?: string
  endTime?: string
  pageNo?: number
  pageSize?: number
  timezone?: string
  /** Legacy filter clauses, passed through verbatim (shape unverified). */
  addFilters?: unknown[]
  /**
   * Queue split, CAPTURED 2026-10-05 (docs/inventory/signals-queue-capture.md):
   * 'unassigned' = signals in NO case, 'assigned' = signals attached to a case.
   * Any other value is ignored by the server (it answers the whole window), so
   * omit the key for "all".
   */
  assignStatus?: SignalAssignStatus
}

export type SignalAssignStatus = 'unassigned' | 'assigned'

export interface SignalsApi {
  list: (q: SignalListQuery) => Promise<{ rows: SignalRow[]; count: number | null }>
  data: (q: SignalScope & { uuid: string }) => Promise<SignalRow>
  timeslice: (q: SignalScope & { duration?: string; startTime?: string; endTime?: string }) => Promise<SignalRow[]>
  kb: (q: SignalScope & { uuid: string }) => Promise<string>
  /** Cases per signal, keyed by signal uuid (the verified live shape). */
  caseList: (q: SignalScope & { uuids: string[] }) => Promise<Record<string, SignalRow[]>>
  addRemark: (q: SignalScope & { uuid: string; remark: string }) => Promise<void>
  /** The scope's signal tags — POST /api/signals/tag/list {scope_id, filter}. */
  tagList: (q: SignalScope & { filter?: string }) => Promise<SignalTag[]>
  /**
   * Add a signal tag to the scope — POST /api/signals/tag/create. The ack
   * carries no tag (`data: {}`), so callers refetch tagList to see it.
   * `color` is a hex string or '' for none; tag_type is always "general".
   */
  tagCreate: (q: SignalScope & { value: string; color: string }) => Promise<void>
  /** Legacy "Tag False Positive": flags the listed signals; `fp: false` untags. */
  flagFalsePositive: (q: SignalScope & { uuids: string[]; fp: boolean }) => Promise<void>
  /** Campaigns behind one connected-signals subgraph — `graph` is the subgraph as the job returned it. */
  attribution: (q: SignalScope & { graph: unknown }) => Promise<AttributionCampaign[]>
  /**
   * Start the signal's raw-event task (facets/task_id). Resolves the task id
   * to poll with the search API's `state`, or null when the signal has no
   * raw-event query. Throws SignalFacetsError on a refusal.
   */
  facetsStart: (q: SignalScope & { uuid: string; timezone?: string }) => Promise<string | null>
  /** The finished task's field breakdown (facets/result). Its events page through the search API's `result`. */
  facetsResult: (q: SignalScope & { taskId: string }) => Promise<SignalFacet[]>
  /** Stop a still-running facets task (wrk/api/job/facetrevoke). */
  facetsRevoke: (q: SignalScope & { taskId: string }) => Promise<void>
}

export function createSignalsApi(client: ApiClient): SignalsApi {
  return {
    list: async ({ clusterId, scopeId, duration = '7d', startTime, endTime, pageNo = 1, pageSize = 100, timezone = 'UTC', addFilters = [], assignStatus }) => {
      const res = await client.request('POST', `/${clusterId}/api/signals/list`, {
        body: {
          scope_id: scopeId,
          duration,
          start_time: startTime ?? '',
          end_time: endTime ?? '',
          pageno: pageNo,
          pagesize: pageSize,
          timezone,
          addfilters: addFilters,
          ...(assignStatus ? { assign_status: assignStatus } : {}),
        },
        schema: signalListResponse,
      })
      return { rows: res.rows, count: res.count }
    },

    tagList: async ({ clusterId, scopeId, filter = '' }) => {
      const res = await client.request('POST', `/${clusterId}/api/signals/tag/list`, {
        body: { scope_id: scopeId, filter },
        schema: signalTagListResponse,
      })
      return res.data
    },

    tagCreate: async ({ clusterId, scopeId, value, color }) => {
      const res = await client.request('POST', `/${clusterId}/api/signals/tag/create`, {
        body: { scope_id: scopeId, tag_value: value, tag_type: 'general', tag_color: color },
        schema: ackEnvelope,
      })
      assertAck(res, 'Creating the signal tag')
    },

    data: async ({ clusterId, scopeId, uuid }) =>
      client.request('POST', `/${clusterId}/api/signals/data`, {
        body: { scope_id: scopeId, signal_uuid: uuid },
        schema: signalDataResponse,
      }),

    timeslice: async ({ clusterId, scopeId, duration = '7d', startTime, endTime }) =>
      client.request('POST', `/${clusterId}/api/signals/timeslice_data`, {
        body: { scope_id: scopeId, duration, start_time: startTime ?? '', end_time: endTime ?? '' },
        schema: signalTimesliceResponse,
      }),

    facetsStart: async ({ clusterId, scopeId, uuid, timezone = 'UTC' }) => {
      const res = await client.request('POST', `/${clusterId}/api/signals/facets/task_id`, {
        body: { query_timezone: timezone, scope_id: scopeId, signal_uuid: uuid },
        schema: facetTaskResponse,
      })
      // A missing status (uncaptured, CB-70) is not a refusal — the same reading as facetsResult.
      if (res.status && res.status !== 'success') throw new SignalFacetsError(res.message || 'The raw-event task did not start')
      const id = res.data && !Array.isArray(res.data) ? res.data.id : undefined
      return id === undefined || id === '' ? null : String(id)
    },

    facetsResult: async ({ clusterId, scopeId, taskId }) => {
      const res = await client.request('POST', `/${clusterId}/api/signals/facets/result`, {
        body: { task_id: taskId, scope_id: scopeId },
        schema: facetResultResponse,
      })
      if (res.status && res.status !== 'success' && !(res.data ?? []).length) {
        throw new SignalFacetsError(res.message || 'The field breakdown failed')
      }
      return readSignalFacets(res.data)
    },

    facetsRevoke: async ({ clusterId, scopeId, taskId }) => {
      await client.request('POST', `/${clusterId}/wrk/api/job/facetrevoke`, {
        body: { task_id: taskId, scope_id: scopeId },
        schema: mutationResponse,
      })
    },

    kb: async ({ clusterId, scopeId, uuid }) =>
      client.request('POST', `/${clusterId}/api/signals/get_kb`, {
        body: { scope_id: scopeId, signal_uuid: uuid },
        schema: signalKbResponse,
      }),

    caseList: async ({ clusterId, scopeId, uuids }) => {
      const res = await client.request('POST', `/${clusterId}/api/signals/case_list`, {
        body: { scope_id: scopeId, signal_list: uuids, pageno: 1, pagesize: 100 },
        schema: signalCaseListResponse,
      })
      return res
    },

    addRemark: async ({ clusterId, scopeId, uuid, remark }) => {
      await client.request('POST', `/${clusterId}/api/signals/add_signal_remark`, {
        body: { scope_id: scopeId, signal_uuid: uuid, signal_remark: remark },
        schema: mutationResponse,
      })
    },

    flagFalsePositive: async ({ clusterId, scopeId, uuids, fp }) => {
      await client.request('POST', `/${clusterId}/api/signals/add_rule`, {
        body: { scope_id: scopeId, signal_list: uuids, Fp: fp },
        schema: mutationResponse,
      })
    },

    attribution: async ({ clusterId, scopeId, graph }) =>
      readSignalAttribution(
        await client.request('POST', `/${clusterId}/api/models/signals_attribution`, {
          body: { scope_id: scopeId, graph_data: graph },
          schema: signalAttributionResponse,
        }),
      ),
  }
}
